import { useEffect, useRef, useState } from 'react'

import '../report.scss'
import ReportEditLogModal from './ReportEditLogModal'
import useTranslations from '../../../hooks/useTranslations'
import ANTDModal from '../../../shared/antd/ANTDModal'
import ANTDSegmented from '../../../shared/antd/ANTDSegmented'
import ANTDTable from '../../../shared/antd/ANTDTable'
import { apiParams } from '../../../utils'
import { downloadReport } from '../../../utils/customFunctions'
import { dayJs, SAVE_FORMAT } from '../../../utils/dayjs'
import { excelIcon, RewindTimeIcon } from '../../../utils/icons'
import { isEqual } from '../../../utils/javascript'
import { getReportHistoryListApi } from '../report.api'

const ViewPreviousHistoryModal = ({
  open,
  onClose,
  payload,
  hideStartDate = false,
  hideEndDate = false,
  showRecoveryTabs = false,
}) => {
  const { t } = useTranslations()
  const [activeSheetType, setActiveSheetType] = useState('PAYMENT_FORM')
  const sheetType = showRecoveryTabs ? activeSheetType : payload?.sheetType
  const isDeliveryChallan = sheetType === 'DELIVERY_CHALLAN'
  const isRecoveryHistory = showRecoveryTabs || sheetType === 'PAYMENT_FORM'
  const [reportLists, setReportLists] = useState({})
  const [loadingSheetTypes, setLoadingSheetTypes] = useState({})
  const [editLogModal, setEditLogModal] = useState({ open: false })
  const reportRequestId = useRef(0)
  const activeReportRequests = useRef({})
  const pageSize = 10
  const reportList = reportLists?.[sheetType] || {}
  const loader = !!loadingSheetTypes?.[sheetType]

  const handleExcelClick = async rowData => {
    if (isEqual(rowData?.status, 'PENDING')) return
    const reportUrl = rowData?.dmsDetails?.fileUrl
    if (reportUrl) await downloadReport(reportUrl)
  }

  const columns = [
    {
      title: t('rpt_StartDate'),
      dataIndex: 'fromDate',
      hidden: hideStartDate,
    },
    {
      title: t('rpt_EndDate'),
      dataIndex: 'toDate',
      hidden: hideEndDate,
    },
    {
      title: t('rpt_CreatedDate'),
      dataIndex: 'modificationDate',
      render: rowData => (rowData ? dayJs(rowData).format(SAVE_FORMAT) : '-'),
    },
    {
      title: t('dvz_Time'),
      dataIndex: 'modificationDate',
      render: rowData => {
        const timeData = rowData ? dayJs(rowData).format('HH:mm:ss') : null
        return !timeData || isEqual(timeData, 'Invalid Date') ? '-' : timeData
      },
      hidden: !isRecoveryHistory,
    },
    {
      title: t('job_Status'),
      dataIndex: 'status',
    },
    {
      title: 'Excel',
      // dataIndex: 'dmsDetails',
      render: rowData => (
        <img
          src={excelIcon}
          alt="excel"
          height={60}
          width={55}
          onClick={() => handleExcelClick(rowData)}
          className={
            rowData?.dmsDetails?.fileUrl
              ? 'cursor-pointer'
              : 'disabled-illusion'
          }
        />
      ),
    },
    {
      title: t('txt_EditLog'),
      key: 'txt_EditLog',
      align: 'center',
      render: rowData => (
        <RewindTimeIcon
          className="history-icon cursor-pointer"
          width={35}
          height={35}
          onClick={() => setEditLogModal({ open: true, rowData })}
        />
      ),
      hidden: !isRecoveryHistory || isDeliveryChallan,
    },
  ].filter(({ hidden }) => !hidden)

  useEffect(() => {
    if (!open) {
      setActiveSheetType('PAYMENT_FORM')
      setReportLists({})
      setLoadingSheetTypes({})
      activeReportRequests.current = {}
    }
  }, [open])

  useEffect(() => {
    if (!open || !sheetType) return
    const hasReportData = Object.prototype.hasOwnProperty.call(
      reportLists,
      sheetType,
    )
    if (!hasReportData && !loadingSheetTypes?.[sheetType]) getReportList({})
  }, [open, sheetType, reportLists, loadingSheetTypes])

  const getReportList = async ({ pageNo = 1 } = {}) => {
    if (!sheetType) return

    const requestedSheetType = sheetType
    const requestId = ++reportRequestId.current
    activeReportRequests.current[requestedSheetType] = requestId
    setLoadingSheetTypes(prev => ({
      ...prev,
      [requestedSheetType]: true,
    }))
    try {
      const response = await getReportHistoryListApi({
        params: apiParams({
          pageNo,
          params: { ...payload, sheetType: requestedSheetType },
        }),
      })
      if (activeReportRequests.current?.[requestedSheetType] === requestId) {
        setReportLists(prev => ({
          ...prev,
          [requestedSheetType]: response?.data || {},
        }))
      }
    } catch (error) {
      if (activeReportRequests.current?.[requestedSheetType] === requestId) {
        setReportLists(prev => ({ ...prev, [requestedSheetType]: {} }))
      }
    } finally {
      if (activeReportRequests.current?.[requestedSheetType] === requestId) {
        delete activeReportRequests.current[requestedSheetType]
        setLoadingSheetTypes(prev => ({
          ...prev,
          [requestedSheetType]: false,
        }))
      }
    }
  }

  const handleTableChange = pagination => {
    getReportList({ pageNo: pagination?.current })
  }

  const handleClose = () => {
    setActiveSheetType('PAYMENT_FORM')
    setReportLists({})
    setLoadingSheetTypes({})
    activeReportRequests.current = {}
    onClose?.()
  }

  return (
    <>
      <ANTDModal
        title={t('rpt_SalesHistorySheet')}
        centered
        open={open}
        onCancel={handleClose}
        footer={false}
        width={1000}
        destroyOnClose={true}
      >
        {showRecoveryTabs && (
          <div className="d-flex justify-center py-15">
            <ANTDSegmented
              className="recovery-history-tabs"
              value={activeSheetType}
              onChange={setActiveSheetType}
              options={[
                { label: t('rpt_PaymentReport'), value: 'PAYMENT_FORM' },
                {
                  label: t('rpt_DeliveryChallan'),
                  value: 'DELIVERY_CHALLAN',
                },
              ]}
            />
          </div>
        )}
        <div className="mt-10">
          <ANTDTable
            dataSource={reportList?.list}
            columns={columns}
            onChange={handleTableChange}
            loading={loader}
            pagination={{
              pageSize,
              lastFetched: reportList?.pageNo,
              current: reportList?.pageNo,
              total: reportList?.lastPage * pageSize,
              responsive: true,
              hideOnSinglePage: true,
            }}
          />
        </div>
      </ANTDModal>
      {editLogModal?.open && (
        <ReportEditLogModal
          open={editLogModal.open}
          onClose={() => setEditLogModal({ open: false })}
          paymentFormEditLogs={editLogModal?.rowData?.paymentFormEditLogs || []}
        />
      )}
    </>
  )
}

export default ViewPreviousHistoryModal
